(() => {
  const STORE_IDS = ['1', '2', '7', '9', '5'];
  const REQUEST_TIMEOUT_MS = 10000;
  const STOCK_FUTURE_TOLERANCE_MS = 30 * 1000;
  const STATE_CLASSES = ['is-loading', 'is-available', 'is-low', 'is-none', 'is-unknown'];
  const VALID_STATES = new Set(['available', 'low', 'none', 'unknown']);

  const normalizeVariantId = (value) => {
    const text = String(value ?? '').trim();
    return /^\d+$/.test(text) ? text : '';
  };

  const parseVariants = (root) => {
    const script = root.querySelector('[data-cm-store-stock-variants]');

    try {
      const variants = JSON.parse(script?.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (_error) {
      return [];
    }
  };

  const parseFetchedAt = (value, nowMs) => {
    const match = typeof value === 'string'
      ? value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.\d{3}Z$/)
      : null;

    if (!match) throw new Error('invalid-fetched-at');

    const [, yearText, monthText, dayText, hourText, minuteText, secondText] = match;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const hour = Number(hourText);
    const minute = Number(minuteText);
    const second = Number(secondText);
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (
      year < 2000 || month < 1 || month > 12 || day < 1 || day > daysInMonth ||
      hour > 23 || minute > 59 || second > 59
    ) {
      throw new Error('invalid-fetched-at');
    }

    const fetchedAtMs = Date.parse(value);
    if (!Number.isFinite(fetchedAtMs)) throw new Error('invalid-fetched-at');

    const ageMs = nowMs - fetchedAtMs;
    if (ageMs < -STOCK_FUTURE_TOLERANCE_MS) throw new Error('invalid-fetched-at');

    return { iso: value, milliseconds: fetchedAtMs };
  };

  const normalizeStockItem = (item) => {
    const state = VALID_STATES.has(item?.state) ? item.state : 'unknown';
    const quantity = item?.quantity;
    const validQuantity = quantity === null || (
      typeof quantity === 'number' && Number.isSafeInteger(quantity) && quantity >= 0
    );

    if (!validQuantity || quantity === null || state === 'unknown') {
      return { state: 'unknown', quantity: null };
    }

    if ((quantity === 0 && state !== 'none') || (quantity > 0 && state === 'none')) {
      return { state: 'unknown', quantity: null };
    }

    return { state, quantity };
  };

  const stocksFromServerItems = (stockItems) => {
    if (!Array.isArray(stockItems)) throw new Error('invalid-store-states');
    const matches = new Map(STORE_IDS.map((storeId) => [storeId, []]));

    stockItems.forEach((item) => {
      const storeId = String(item?.storeId ?? '').trim();
      if (matches.has(storeId)) matches.get(storeId).push(item);
    });

    return new Map(STORE_IDS.map((storeId) => {
      const itemsForId = matches.get(storeId);
      const stock = itemsForId.length === 1
        ? normalizeStockItem(itemsForId[0])
        : { state: 'unknown', quantity: null };
      return [storeId, stock];
    }));
  };

  const parseSmaregiServerResponse = (payload, requestedVariantId, nowMs = Date.now()) => {
    if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
      throw new Error('server-error');
    }

    const result = payload?.data || payload;
    const responseVariantId = normalizeVariantId(result?.variantId);

    if (
      !['smaregi-api', 'logiless-api'].includes(result?.source) ||
      !responseVariantId ||
      responseVariantId !== requestedVariantId
    ) {
      throw new Error('variant-mismatch');
    }

    return {
      fetchedAt: parseFetchedAt(result.fetchedAt, nowMs),
      stocks: stocksFromServerItems(result.stockItems)
    };
  };

  const formatQuantityLabel = (template, quantity) => {
    if (!Number.isSafeInteger(quantity) || quantity < 0) return '';
    return String(template || '__QUANTITY__').replace('__QUANTITY__', String(quantity));
  };

  const SYMBOL_HIGH_STOCK = 3;
  const SYMBOL_LOW_STOCK = 0;

  const stockSymbolFor = (quantity) => {
    if (!Number.isSafeInteger(quantity) || quantity < 0) return null;
    if (quantity >= SYMBOL_HIGH_STOCK) return { symbol: '◯', state: 'available', labelKey: 'labelStatusAvailable' };
    if (quantity <= SYMBOL_LOW_STOCK) return { symbol: '×', state: 'none', labelKey: 'labelStatusNone' };
    return { symbol: '△', state: 'low', labelKey: 'labelStatusLow' };
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = Object.freeze({
      parseSmaregiServerResponse,
      formatQuantityLabel,
      stockSymbolFor
    });
    return;
  }

  const existingController = window.CMStoreStockController;

  if (existingController) {
    existingController.scan();
    return;
  }

  const postJson = async (endpoint, body, signal) => {
    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal
    });

    if (!response.ok) throw new Error(`http-${response.status}`);
    return response.json();
  };

  const SOURCE_ADAPTERS = Object.freeze({
    'smaregi-server': {
      requiresEndpoint: true,
      async read({ endpoint, variantId, productId, signal }) {
        const payload = await postJson(endpoint, {
          shopifyProductId: productId,
          shopifyProductVariantId: variantId
        }, signal);
        return parseSmaregiServerResponse(payload, variantId);
      }
    },
    'smaregi-shopify-metafield': {
      requiresEndpoint: false,
      async read({ stockSnapshot, variantId }) {
        return parseSmaregiServerResponse(stockSnapshot, variantId);
      }
    }
  });

  class StoreStockDialog {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-cm-store-stock-dialog]');
      this.triggerTemplate = root.querySelector('[data-cm-store-stock-trigger-template]');
      this.live = root.querySelector('[data-cm-store-stock-live]');
      this.reloadButton = root.querySelector('[data-cm-store-stock-reload]');
      this.imageWrap = root.querySelector('[data-cm-store-stock-image-wrap]');
      this.image = root.querySelector('[data-cm-store-stock-image]');
      this.options = root.querySelector('[data-cm-store-stock-options]');
      this.productId = normalizeVariantId(root.dataset.productId);
      this.endpoint = root.dataset.endpoint;
      this.productImage = root.dataset.productImage;
      this.sourceName = root.dataset.stockSource;
      this.sourceStatus = root.dataset.sourceStatus;
      this.variants = new Map();
      this.currentVariant = null;
      this.abortController = null;
      this.requestToken = 0;
      this.previousFocus = null;
      this.scrollPosition = { x: 0, y: 0, containers: [] };
      this.boundClose = this.close.bind(this);
      this.boundReload = this.reload.bind(this);
      this.boundDialogClick = this.onDialogClick.bind(this);
      this.boundDialogClose = this.onDialogClose.bind(this);

      parseVariants(root).forEach((variant) => {
        const variantId = normalizeVariantId(variant?.id);
        if (variantId) this.variants.set(variantId, variant);
      });

      const selectedVariantId = normalizeVariantId(root.dataset.selectedVariantId);
      this.currentVariant = this.variants.get(selectedVariantId) || null;

      root.querySelector('[data-cm-store-stock-close]')?.addEventListener('click', this.boundClose);
      this.reloadButton?.addEventListener('click', this.boundReload);
      this.dialog?.addEventListener('click', this.boundDialogClick);
      this.dialog?.addEventListener('close', this.boundDialogClose);
    }

    destroy() {
      this.cancelRequest();
      this.requestToken += 1;
      const wasOpen = Boolean(this.dialog?.open);
      if (wasOpen) this.dialog.close();
      this.root.querySelector('[data-cm-store-stock-close]')?.removeEventListener('click', this.boundClose);
      this.reloadButton?.removeEventListener('click', this.boundReload);
      this.dialog?.removeEventListener('click', this.boundDialogClick);
      this.dialog?.removeEventListener('close', this.boundDialogClose);
      if (wasOpen && !document.querySelector('[data-cm-store-stock-dialog][open]')) {
        document.documentElement.classList.remove('cm-store-stock-is-open');
        this.restoreScrollPosition();
      }
    }

    isMainProductForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;
      const formId = form.getAttribute('id') || '';
      if (!(form.getAttribute('action') || '').includes('/cart/add')) return false;
      if (formId.endsWith('-payment-installment')) return false;
      return formId.startsWith('cm-sales-product-form-') || formId.startsWith(`product-form-main-${this.productId}-`);
    }

    variantFromId(value) {
      const variantId = normalizeVariantId(value);
      return variantId ? this.variants.get(variantId) || null : null;
    }

    mainPickerVariantIds() {
      return Array.from(document.querySelectorAll('variant-picker[context="main_product"]')).flatMap((picker) => {
        const sectionId = picker.getAttribute('section-id') || '';
        const formId = picker.getAttribute('form-id') || '';
        if (!/^template--\d+__c-product-main$/.test(sectionId) || formId !== `product-form-${sectionId}`) return [];
        const section = document.getElementById(`shopify-section-${sectionId}`);
        const form = document.getElementById(formId);
        if (!section?.contains(picker) || !(form instanceof HTMLFormElement) || !section.contains(form)) return [];
        if (!(form.getAttribute('action') || '').includes('/cart/add')) return [];

        const scripts = picker.querySelectorAll('script[data-variant]');
        if (scripts.length !== 1 || scripts[0].getAttribute('type') !== 'application/json') return [''];
        try {
          return [normalizeVariantId(JSON.parse(scripts[0].textContent)?.id)];
        } catch (_error) {
          return [''];
        }
      });
    }

    resolveFreshVariant() {
      const candidateForms = Array.from(document.querySelectorAll('form[action*="/cart/add"]')).filter((form) => this.isMainProductForm(form));

      for (const form of candidateForms) {
        const input = form.querySelector('[name="id"]:not([disabled])');
        if (input) return this.variantFromId(input.value);
      }

      const pickerVariantIds = this.mainPickerVariantIds();
      if (pickerVariantIds.length) {
        return new Set(pickerVariantIds).size === 1 ? this.variantFromId(pickerVariantIds[0]) : null;
      }

      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      return this.variantFromId(urlVariant);
    }

    setCurrentVariant(variant) {
      this.currentVariant = variant || null;
      const optionValues = Array.isArray(variant?.options)
        ? variant.options.filter((option) => String(option ?? '').trim() !== '')
        : [];
      const optionText = optionValues.join(' / ');
      const optionTemplate = this.root.dataset.labelOptionTemplate || '__OPTIONS__';

      if (this.options) {
        this.options.textContent = variant && optionText
          ? optionTemplate.replace('__OPTIONS__', optionText)
          : this.root.dataset.labelVariantUnknown;
      }

      const variantImage = variant?.featured_image?.src || variant?.featured_image || this.productImage;

      if (this.image && this.imageWrap) {
        if (typeof variantImage === 'string' && variantImage.trim() !== '') {
          this.image.src = variantImage;
          this.imageWrap.hidden = false;
        } else {
          this.image.removeAttribute('src');
          this.imageWrap.hidden = true;
        }
      }
    }

    restoreScrollPosition() {
      this.scrollPosition.containers.forEach(({ element, left, top }) => {
        if (element.isConnected) element.scrollTo({ left, top, behavior: 'instant' });
      });
      window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
    }

    open(trigger) {
      if (!this.dialog) return;
      this.previousFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;

      if (!this.dialog.open) {
        const containers = [];
        for (let element = this.root.parentElement; element && element !== document.documentElement; element = element.parentElement) {
          if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) {
            containers.push({ element, left: element.scrollLeft, top: element.scrollTop });
          }
        }
        this.scrollPosition = { x: window.scrollX, y: window.scrollY, containers };
        this.dialog.showModal();
        document.documentElement.classList.add('cm-store-stock-is-open');
        this.restoreScrollPosition();
      }

      const variant = this.resolveFreshVariant();
      this.setCurrentVariant(variant);

      if (!variant) {
        this.cancelRequest();
        this.requestToken += 1;
        this.renderAll('unknown');
        this.setLive(this.root.dataset.labelVariantUnknown, true);
        return;
      }

      this.fetchStock(normalizeVariantId(variant.id));
    }

    close() {
      if (this.dialog?.open) this.dialog.close();
    }

    onDialogClick(event) {
      if (event.target === this.dialog) this.close();
    }

    onDialogClose() {
      this.cancelRequest();
      this.requestToken += 1;

      if (!document.querySelector('[data-cm-store-stock-dialog][open]')) {
        document.documentElement.classList.remove('cm-store-stock-is-open');
      }

      this.restoreScrollPosition();

      if (this.previousFocus instanceof HTMLElement && this.previousFocus.isConnected) {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    reload() {
      if (this.sourceName === 'smaregi-shopify-metafield') {
        window.location.reload();
        return;
      }

      const variant = this.resolveFreshVariant();
      this.setCurrentVariant(variant);

      if (!variant) {
        this.cancelRequest();
        this.requestToken += 1;
        this.renderAll('unknown');
        this.setLive(this.root.dataset.labelVariantUnknown, true);
        return;
      }

      this.fetchStock(normalizeVariantId(variant.id));
    }

    cancelRequest() {
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
    }

    async fetchStock(variantId) {
      this.cancelRequest();
      const requestToken = ++this.requestToken;

      const sourceAdapter = SOURCE_ADAPTERS[this.sourceName];

      if (
        !sourceAdapter ||
        this.sourceStatus !== 'connected' ||
        (sourceAdapter.requiresEndpoint && !this.endpoint)
      ) {
        this.renderAll('unknown');
        this.setLive(this.root.dataset.labelSourceUnavailable, false);
        return;
      }

      const controller = new AbortController();
      let timedOut = false;
      this.abortController = controller;
      this.renderAll('loading');
      this.setLive(this.root.dataset.labelLoading, false);
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, REQUEST_TIMEOUT_MS);

      try {
        const result = await sourceAdapter.read({
          endpoint: this.endpoint,
          variantId,
          productId: this.productId,
          stockSnapshot: this.currentVariant?.storeStock,
          signal: controller.signal
        });

        if (requestToken !== this.requestToken || !this.dialog?.open) return;
        this.renderStocks(result.stocks);
        const hasUnknown = Array.from(result.stocks.values()).some((stock) => stock.state === 'unknown');
        this.setLive(this.loadedMessage(result.fetchedAt.milliseconds, hasUnknown), hasUnknown);
      } catch (error) {
        if (requestToken !== this.requestToken || !this.dialog?.open) return;
        if (error?.name === 'AbortError' && !timedOut) return;
        this.renderAll('unknown');
        this.setLive(this.root.dataset.labelError, true);
      } finally {
        window.clearTimeout(timeoutId);
        if (requestToken === this.requestToken) this.abortController = null;
      }
    }

    loadedMessage(fetchedAtMs, hasUnknown) {
      const locale = document.documentElement.lang || 'ja-JP';
      const time = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(fetchedAtMs));
      const updated = String(this.root.dataset.labelUpdatedTemplate || '__TIME__').replace('__TIME__', time);
      const status = hasUnknown ? this.root.dataset.labelPartial : this.root.dataset.labelLoaded;
      return `${status} ${updated}`.trim();
    }

    renderStocks(stocks) {
      STORE_IDS.forEach((storeId) => {
        this.renderStock(storeId, stocks.get(storeId) || { state: 'unknown', quantity: null });
      });
    }

    renderAll(state) {
      STORE_IDS.forEach((storeId) => this.renderStock(storeId, { state, quantity: null }));
    }

    renderStock(storeId, stock) {
      const row = this.root.querySelector(`[data-store-id="${storeId}"]`);
      const status = row?.querySelector('[data-cm-store-stock-status]');
      const label = row?.querySelector('[data-cm-store-stock-status-label]');

      if (!status || !label) return;
      const state = stock?.state === 'loading'
        ? 'loading'
        : (VALID_STATES.has(stock?.state) ? stock.state : 'unknown');
      const display = state === 'loading' || state === 'unknown' ? null : stockSymbolFor(stock?.quantity);
      status.classList.remove(...STATE_CLASSES);
      status.classList.add(`is-${display ? display.state : (state === 'loading' ? 'loading' : 'unknown')}`);
      if (state === 'loading') {
        label.textContent = this.root.dataset.labelLoading;
      } else if (display) {
        const part = document.createElement('span');
        part.className = 'cm-store-stock__quantity-number';
        part.textContent = display.symbol;
        const statusText = this.root.dataset[display.labelKey];
        if (statusText) {
          part.setAttribute('role', 'img');
          part.setAttribute('aria-label', statusText);
        }
        label.replaceChildren(part);
      } else {
        label.textContent = this.root.dataset.labelStatusUnknown;
      }
    }

    setLive(message, canReload) {
      if (this.live) this.live.textContent = message || '';
      if (this.reloadButton) this.reloadButton.hidden = !canReload;
    }

    receiveVariantEvent(event, eventName) {
      const detail = event?.detail;
      if (!detail) return;

      if (eventName === 'cm:variant-change') {
        if (String(detail.productId ?? '') !== this.productId) return;
      } else if (!this.isMainProductForm(event.target)) {
        return;
      }

      const variant = this.variantFromId(detail.variantId || detail.variant?.id);
      if (!variant) {
        this.setCurrentVariant(null);
        if (this.dialog?.open) {
          this.cancelRequest();
          this.requestToken += 1;
          this.renderAll('unknown');
          this.setLive(this.root.dataset.labelVariantUnknown, true);
        }
        return;
      }

      const previousId = normalizeVariantId(this.currentVariant?.id);
      const nextId = normalizeVariantId(variant.id);
      this.setCurrentVariant(variant);

      if (this.dialog?.open && previousId !== nextId) this.fetchStock(nextId);
    }
  }

  const controller = {
    instances: new Map(),
    observer: null,

    scan(scope = document) {
      const roots = [];
      if (scope instanceof Element && scope.matches('[data-cm-store-stock]')) roots.push(scope);
      if ('querySelectorAll' in scope) roots.push(...scope.querySelectorAll('[data-cm-store-stock]'));

      roots.forEach((root) => {
        if (!this.instances.has(root)) this.instances.set(root, new StoreStockDialog(root));
      });

      this.enhanceTriggers(scope);
    },

    enhanceTriggers(scope = document) {
      const triggers = [];
      if (scope instanceof Element && scope.matches('a[href="#stock"]')) triggers.push(scope);
      if ('querySelectorAll' in scope) triggers.push(...scope.querySelectorAll('a[href="#stock"]'));

      triggers.forEach((trigger) => {
        if (trigger.dataset.cmStoreStockEnhanced === 'true') return;
        const instance = this.instanceForTrigger(trigger);
        if (!instance?.triggerTemplate) return;
        trigger.replaceChildren(instance.triggerTemplate.content.cloneNode(true));
        trigger.classList.add('cm-store-stock-trigger');
        trigger.dataset.cmStoreStockEnhanced = 'true';
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', instance.dialog.id);
      });
    },

    instanceForTrigger(trigger) {
      const targetId = trigger.getAttribute('href')?.slice(1);
      const root = targetId ? document.getElementById(targetId) : null;
      return root ? this.instances.get(root) || null : null;
    },

    onClick(event) {
      const trigger = event.target.closest?.('a[href="#stock"]');
      if (!trigger) return;
      const instance = this.instanceForTrigger(trigger);
      if (!instance) return;
      event.preventDefault();
      instance.open(trigger);
    },

    onVariant(event) {
      const eventName = event.type;
      this.instances.forEach((instance) => instance.receiveVariantEvent(event, eventName));
      window.requestAnimationFrame(() => this.enhanceTriggers(document));
    },

    onSectionLoad(event) {
      this.scan(event.target);
    },

    onSectionUnload(event) {
      const roots = [];
      if (event.target instanceof Element && event.target.matches('[data-cm-store-stock]')) roots.push(event.target);
      roots.push(...event.target.querySelectorAll('[data-cm-store-stock]'));
      roots.forEach((root) => {
        this.instances.get(root)?.destroy();
        this.instances.delete(root);
      });
    },

    observe() {
      if (this.observer || !document.body) return;
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) this.scan(node);
        }));
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  window.CMStoreStockController = controller;
  document.addEventListener('click', controller.onClick.bind(controller), { capture: true });
  document.addEventListener('cm:variant-change', controller.onVariant.bind(controller));
  document.addEventListener('variant:change', controller.onVariant.bind(controller));
  document.addEventListener('shopify:section:load', controller.onSectionLoad.bind(controller));
  document.addEventListener('shopify:section:unload', controller.onSectionUnload.bind(controller));
  controller.scan();
  controller.observe();
})();
